import {keyToFilterField} from '../../../forecast-app/shared/components/filters/filter_util';
import {
	BUDGET_TYPE,
	FILTER_TYPE,
	GLOBAL_FILTER_FIELD,
	PHASE_BREAKDOWN_TYPE,
	ROLE_BREAKDOWN_TYPE,
	GLOBAL_FILTER_OPERATOR,
} from '../../../constants';
import Util from '../../../forecast-app/shared/util/util';
import {getUnifiedFilterOperator, isOperator} from '../../../forecast-app/shared/components/filters/filter_logic';

export const convertProjectSearchFilters = filters => {
	const updatedFilters = [];

	if (filters !== null && filters.project) {
		const projectFilters = filters.project;
		for (let key of Object.keys(projectFilters)) {
			if (isOperator(key)) {
				continue;
			}
			const rawValue = filters.project[key];
			let values = rawValue;
			if (!Array.isArray(values)) {
				values = [values];
			}
			values = values.map(value => {
				if (!value) {
					return '';
				} else return value;
			});

			const unifiedFilterOperator = getUnifiedFilterOperator(key, filters.project);

			if (key === FILTER_TYPE.PROJECT_TYPE) {
				const budgetTypeValueMap = {
					fixed_price: 'FIXED_PRICE',
					'non-billable': 'NON_BILLABLE',
					time_and_material: 'TIME_AND_MATERIALS',
					retainers: 'RETAINER',
				};
				updatedFilters.push({
					field: GLOBAL_FILTER_FIELD.PROJECT_BUDGET_TYPE,
					operator: unifiedFilterOperator,
					value: budgetTypeValueMap[rawValue],
				});
			} else {
				updatedFilters.push({
					field: keyToFilterField(key),
					operator: unifiedFilterOperator,
					value: values,
				});
			}
		}
	}

	return updatedFilters;
};

const getWinChance = (values, unifiedFilterOperator) => {
	// This logic is sort of redundent now since we can only choose one project win chance
	// in the filters. Keeping in case we decide to switch back.

	const mapping = {
		ZERO: 0,
		OVER_10: 0.1,
		OVER_30: 0.3,
		OVER_50: 0.5,
		OVER_70: 0.7,
		OVER_90: 0.9,
		HUNDRED: 1,
	};

	if (!values?.length) return null;

	// If IN return mapped numbers as strings. For Project win chance we only
	// use the IN operator when we have both 0% an 100% which means we
	// do not have to get the highest or lowest win chance.
	if (unifiedFilterOperator === GLOBAL_FILTER_OPERATOR.IN) {
		return values.map(v => String(mapping[v]));
	}

	let numericValues = values.map(v => mapping[v]);

	// Exclude ZERO and HUNDRED if operator is LESS or GREATER. If the operator is LESS or
	// GREATER it means we have at lest one over_x filter and if we also have 0% or 100% as the over
	// just takes priority.
	if (unifiedFilterOperator === GLOBAL_FILTER_OPERATOR.LESS || unifiedFilterOperator === GLOBAL_FILTER_OPERATOR.GREATER) {
		numericValues = numericValues.filter(v => v !== 0 && v !== 1);
	}

	return unifiedFilterOperator === GLOBAL_FILTER_OPERATOR.LESS
		? String(Math.max(...numericValues))
		: String(Math.min(...numericValues));
};

export const filtersToSearchQuery = (filters, addedFilters) => {
	const searchQuery = {
		filters: [],
	};
	if (addedFilters) {
		addedFilters.forEach(filter => {
			searchQuery.filters.push({
				field: keyToFilterField(filter.key),
				operator: filter.operator,
				value: filter.value,
			});
		});
	}

	if (filters?.project) {
		for (const [key, value] of Object.entries(filters.project)) {
			if (isOperator(key)) {
				continue;
			}
			const unifiedFilterOperator = getUnifiedFilterOperator(key, filters.project);
			let resolvedValue;
			if (key === 'projectType') {
				resolvedValue = value.map(filterBudgetType => {
					switch (filterBudgetType) {
						case 'time_and_material':
							return BUDGET_TYPE.TIME_AND_MATERIALS;
						case 'retainers':
							return BUDGET_TYPE.RETAINER;
						case 'fixed_price':
							return BUDGET_TYPE.FIXED_PRICE;
						case 'non-billable':
							return BUDGET_TYPE.NON_BILLABLE;
						default:
							throw new Error(`Unknown budgetType ${filterBudgetType}`);
					}
				});
			} else if (key === 'project_win_chance') {
				resolvedValue = getWinChance(value, unifiedFilterOperator);
			} else {
				resolvedValue = value;
			}

			searchQuery.filters.push({
				field: keyToFilterField(key),
				operator: unifiedFilterOperator,
				value: resolvedValue,
			});
		}
	}
	return searchQuery;
};

export const getPhaseName = (intl, phaseRowData) => {
	if (phaseRowData.breakdownType === PHASE_BREAKDOWN_TYPE.NO_PHASE) {
		return intl.formatMessage({id: 'project_scopes.no-scope'});
	} else if (phaseRowData.breakdownType === PHASE_BREAKDOWN_TYPE.PROJECT_TIME_REG) {
		return intl.formatMessage({id: 'project_budget.project_time_entries_with_no_tasks'});
	} else if (phaseRowData.breakdownType === PHASE_BREAKDOWN_TYPE.OFFSET) {
		return intl.formatMessage({id: 'common.offset'});
	} else if (phaseRowData.breakdownType === PHASE_BREAKDOWN_TYPE.ACCUMULATED_TOTALS) {
		return intl.formatMessage({id: 'project_budget.accumulated_totals'});
	} else {
		return phaseRowData.phase ? phaseRowData.phase.name : '';
	}
};

export const getRoleName = (intl, roleRowData) => {
	if (roleRowData.breakdownType === ROLE_BREAKDOWN_TYPE.NO_ROLE) {
		return intl.formatMessage({id: 'card_modal.no-role'});
	} else if (roleRowData.breakdownType === ROLE_BREAKDOWN_TYPE.EXPENSES) {
		return intl.formatMessage({id: 'project_budget.expenses'});
	} else {
		return roleRowData.role ? roleRowData.role.name : '';
	}
};

export const getExpenseCategoryName = (intl, expenseCategoryRowData) => {
	return expenseCategoryRowData.expenseCategory ? expenseCategoryRowData.expenseCategory.name : '';
};

export const sortBreakdownPhases = phaseFinancialBreakdown => {
	phaseFinancialBreakdown.sort((a, b) => {
		if (a.breakdownType === PHASE_BREAKDOWN_TYPE.PHASE && b.breakdownType === PHASE_BREAKDOWN_TYPE.PHASE) {
			const startDateA = Util.CreateMomentDate(a.phase.startYear, a.phase.startMonth, a.phase.startDay);
			const startDateB = Util.CreateMomentDate(b.phase.startYear, b.phase.startMonth, b.phase.startDay);
			return startDateA - startDateB;
		}
		return 0;
	});

	phaseFinancialBreakdown.sort((a, b) => {
		return (a.phase?.sortOrder || Number.MAX_SAFE_INTEGER) - (b.phase?.sortOrder || Number.MAX_SAFE_INTEGER);
	});

	const typeSortOrder = ['PHASE', 'NO_PHASE', 'PROJECT_TIME_REG'];
	phaseFinancialBreakdown.sort((a, b) => {
		const typeIndexA = typeSortOrder.indexOf(a.breakdownType);
		const typeIndexB = typeSortOrder.indexOf(b.breakdownType);
		return typeIndexA - typeIndexB;
	});
};

export const sortBreakdownRoles = roleBreakdownData => {
	const typeSortOrder = ['ROLE', 'NO_ROLE', 'EXPENSES'];
	roleBreakdownData.sort((a, b) => {
		const typeIndexA = typeSortOrder.indexOf(a.breakdownType);
		const typeIndexB = typeSortOrder.indexOf(b.breakdownType);

		let comparison = typeIndexA - typeIndexB;

		if (comparison === 0 && a.breakdownType === ROLE_BREAKDOWN_TYPE.ROLE) {
			if (!a.role) return 1;
			if (!b.role) return -1;
			return a.role.name - b.role.name;
		}

		return comparison;
	});
};
